/**
 * Google Ads Page — Phase 2 read-only dashboard.
 *
 * Layout (top → bottom):
 *   1. Status bar:  prompt sigil + selected account + currency + reload
 *   2. Account picker: dropdown of the accessible-customers list
 *   3. KPI cards: last-30-day cost / impressions / clicks / conversions
 *   4. Campaign table: top 100 by cost, aggregated over last 30 days
 *   5. Daily-performance table: per-day account totals, last 30 days
 *
 * Data freshness: live fetch on every account-switch / reload. No caching.
 *
 * Notes on safety / UX (informed by the pre-deploy review):
 * - Concurrent fetches are guarded by a run-id ref so a slow A response
 *   never lands after the user picked B (the page would otherwise show
 *   B in the header but A in the tables).
 * - On reload we keep the previous data visible while the refresh runs.
 *   On account-switch we clear it — different intent, different UX.
 * - Auto-selection prefers the configured defaultCustomerId, then the
 *   first non-manager row, only falling back to list[0] when neither
 *   applies. Manager accounts can't run campaign / metrics queries (Ads
 *   API returns INVALID_ARGUMENT), so the page short-circuits when the
 *   selected id matches the configured manager and shows an explainer
 *   instead of a blank dashboard with a yellow error.
 * - Currency is taken from the loaded customer_summary, NOT hard-coded.
 *   If the summary hasn't loaded, currency-bearing cells render as plain
 *   decimals rather than misleadingly showing AUD on a USD account.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, doc, onSnapshot, query as fsQuery, where } from 'firebase/firestore';
import { PageTitle } from '../../../_metronic/layout/core';
import { db } from '../../../firebase';
import { authFetch } from '../../utils/cloudFunctionsAuth';
import { useAppData } from '../../providers/AppDataProvider';
import { PortfolioDashboard } from './PortfolioDashboard';
import { AccountDeepSections, Tab as DeepTab } from './AccountDeepSections';
import { AccountAlertsPanel, AlertDoc } from './AdsAlerts';
import { ChangeActivityPanel } from './ChangeActivityPanel';
import { CampaignWizard } from './CampaignWizard';
import { AdsRangeSelector } from './AdsRangeSelector';
import { AdsTrendChart, TrendPoint, TrendMetric, TREND_METRICS } from './AdsTrendChart';
import { loadAdsAccountList, AdsRange, loadStoredAdsRange, storeAdsRange, ensureAdsRangeBackfill, monthsBetween, unpackMonthDoc, proposeChange, startGoogleAdsReconnect, looksLikeAdsAuthError } from './googleAdsApi';
import { toast } from 'react-toastify';
import './GoogleAdsPage.scss';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

interface AccessibleCustomer {
  customerId: string;
  // Optional to stay assignable from the shared googleAdsApi type, whose
  // loadAdsAccountList synthesises rows for MCC-only descendants.
  resourceName?: string;
}

/** Enrichment from customer_clients_under_manager. Populated for accounts
 *  that are descendants of the configured MCC; absent for accounts the
 *  user has direct access to that aren't under that manager. The `manager`
 *  boolean is the critical bit — selecting a sub-MCC means metric queries
 *  WILL fail with REQUESTED_METRICS_FOR_MANAGER, so the picker filters
 *  them out of auto-selection and labels them visibly. */
interface EnrichedAccount {
  descriptiveName?: string;
  manager?: boolean;
  testAccount?: boolean;
  status?: string;
  currencyCode?: string;
  level?: number;
}

interface CustomerSummaryRow {
  customer: {
    id: string;
    descriptiveName?: string;
    currencyCode?: string;
    timeZone?: string;
    status?: string;
    manager?: boolean;
    testAccount?: boolean;
    autoTaggingEnabled?: boolean;
  };
}

interface CampaignRow {
  campaign: {
    id: string;
    name: string;
    status: string;
    advertisingChannelType: string;
  };
  impressionShare?: { search?: number | null; budgetLost?: number | null; rankLost?: number | null } | null;
  campaignBudget?: { amountMicros?: string };
  metrics?: {
    costMicros?: string;
    impressions?: string;
    clicks?: string;
    conversions?: number;
    conversionsValue?: number;
    ctr?: number;
    averageCpc?: string;
  };
}

interface DailyPerformanceRow {
  segments: { date: string };
  metrics?: {
    costMicros?: string;
    impressions?: string;
    clicks?: string;
    conversions?: number;
    conversionsValue?: number;
  };
}

interface QueryResponse<T> {
  ok: boolean;
  rows?: T[];
  error?: string;
  /** Parsed GoogleAdsFailure enum like 'queryError.REQUESTED_METRICS_FOR_MANAGER'
   *  or 'authorizationError.USER_PERMISSION_DENIED'. The page branches on
   *  this to auto-recover from picking a sub-MCC. */
  errorCode?: string;
  httpStatus?: number;
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

const breadCrumbs = [
  { title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
];

/** Google returns int64 fields as JSON strings (cost_micros, impressions,
 *  etc.). Coerce to number once at the boundary so the rest of the page
 *  can do plain arithmetic without `Number(...)` everywhere. */
function n(value: string | number | undefined | null): number {
  if (value === undefined || value === null || value === '') return 0;
  const num = Number(value);
  return isFinite(num) ? num : 0;
}

/** cost_micros → currency. 1,234,567,890 micros = $1,234.57. */
function microsToAmount(micros: string | number | undefined): number {
  return n(micros) / 1_000_000;
}

/** Format a monetary amount. When `currency` is omitted (customer_summary
 *  hasn't loaded yet) we render the raw decimal instead of guessing AUD —
 *  on a USD/EUR account the assumed-AUD label was actively misleading. */
function formatCurrency(amount: number, currency?: string): string {
  if (!currency) return formatDecimal(amount, 2);
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${formatDecimal(amount, 2)} ${currency}`;
  }
}

function formatInt(amount: number): string {
  return new Intl.NumberFormat('en-AU').format(Math.round(amount));
}

function formatDecimal(amount: number, digits = 2): string {
  return new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
}

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(2)}%`;
}

function prettyCustomerId(id: string): string {
  const digits = String(id || '').replace(/\D+/g, '');
  if (digits.length !== 10) return digits;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
}

function lower(s: string | undefined): string {
  return String(s || '').toLowerCase().replace(/_/g, ' ');
}

/** A campaign is "not serving" when it's PAUSED or REMOVED. Deliberately NOT
 *  called "disabled": that word already means the app-side per-ACCOUNT flag
 *  (disabledCids / the portfolio's disable button), and having it mean two
 *  unrelated things on adjacent surfaces was genuinely confusing.
 *  Module-scope so the campaigns-table filter can use it inside memos without
 *  churning their dependency lists. */
function isCampaignNotServing(status: string | undefined): boolean {
  const st = String(status || '').toUpperCase();
  return st === 'PAUSED' || st === 'REMOVED';
}

type CampaignStatusFilter = 'active' | 'notServing' | 'all';

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────

const GoogleAdsPage: React.FC = () => {
  // ── Account list + config ──────────────────────────────────────────
  const [accounts, setAccounts] = useState<AccessibleCustomer[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [accountsLoading, setAccountsLoading] = useState(true);
  /** Manager / MCC customer id from saved config. The page uses this to
   *  flag the manager row in the picker and short-circuit metric queries
   *  when it's selected. */
  const [managerCid, setManagerCid] = useState<string>('');
  /** Pre-configured default customer id. Wins over first-non-manager
   *  in auto-selection. */
  const [defaultCid, setDefaultCid] = useState<string>('');
  /** Map of customer id → enrichment from customer_clients query. Empty
   *  for accounts outside the configured MCC's tree (we can't tell what
   *  they are without per-account summary queries). Drives picker labels
   *  + auto-select avoidance of sub-managers. */
  const [enriched, setEnriched] = useState<Record<string, EnrichedAccount>>({});
  /** Customer ids the page has discovered to be managers, either via the
   *  customer_clients query OR via a summary that returned manager=true
   *  OR via a query that failed with REQUESTED_METRICS_FOR_MANAGER. Used
   *  by auto-select so we never bounce into the same manager twice. */
  const [knownManagers, setKnownManagers] = useState<Set<string>>(() => new Set());
  /** Per-account → ancestor-manager cid to pass as login-customer-id.
   *  Built by the multi-manager discovery in loadAdsAccountList so accounts
   *  under a manager OTHER than the configured one are still queryable. */
  const [managerOf, setManagerOf] = useState<Record<string, string>>({});

  // ── Selected account + data ────────────────────────────────────────
  const [selectedCid, setSelectedCid] = useState<string>('');
  /** Customer ids the user has disabled (app-side flag in the store). The
   *  picker + auto-select exclude these so disabled accounts never show in
   *  the dropdown. Live subscription so toggling disable on the portfolio
   *  reflects here immediately. */
  const [disabledCids, setDisabledCids] = useState<Set<string>>(() => new Set());
  const [summary, setSummary] = useState<CustomerSummaryRow | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [campAction, setCampAction] = useState<{ kind: 'status' | 'budget'; c: CampaignRow } | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [campApplying, setCampApplying] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const openCampStatus = useCallback((c: CampaignRow) => setCampAction({ kind: 'status', c }), []);
  const openCampBudget = useCallback((c: CampaignRow) => { setBudgetInput((microsToAmount(c.campaignBudget?.amountMicros) || 0).toFixed(2)); setCampAction({ kind: 'budget', c }); }, []);
  // Alert click-through state (handler defined after the noActivity gate below,
  // since it needs to know whether the deep sections are even mounted).
  const [deepFocus, setDeepFocus] = useState<{ tab: DeepTab; wasteOnly?: boolean; adApproval?: 'disapproved'; nonce: number; cid?: string } | null>(null);
  const focusNonce = useRef(0);
  const campaignsRef = useRef<HTMLDivElement | null>(null);
  const applyCampAction = useCallback(async () => {
    if (!campAction || !selectedCid) return;
    const c = campAction.c;
    let op: any;
    if (campAction.kind === 'status') {
      const cur = String(c.campaign.status || '').toUpperCase() === 'PAUSED' ? 'PAUSED' : 'ENABLED';
      op = { type: 'campaign.setStatus', campaignId: c.campaign.id, status: cur === 'PAUSED' ? 'ENABLED' : 'PAUSED', beforeStatus: cur, entityLabel: c.campaign.name };
    } else {
      const amt = parseFloat(budgetInput);
      if (!isFinite(amt) || amt <= 0) { toast.error('Enter a valid daily budget.'); return; }
      op = { type: 'campaign.setBudget', campaignId: c.campaign.id, amountMicros: Math.round(amt * 1e6), entityLabel: c.campaign.name };
    }
    setCampApplying(true);
    try {
      const r = await proposeChange(selectedCid, [op], { apply: true, kind: op.type, source: 'direct' });
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success('Applied to Google Ads. Undo from “change activity” at the bottom of this page.');
      // Optimistic: reflect the change in the campaign table immediately (the
      // store catches up on the next sync).
      if (op.type === 'campaign.setStatus') {
        setCampaigns((prev) => prev.map((cc) => cc.campaign.id === op.campaignId ? { ...cc, campaign: { ...cc.campaign, status: op.status } } : cc));
      } else {
        setCampaigns((prev) => prev.map((cc) => cc.campaign.id === op.campaignId ? { ...cc, campaignBudget: { ...(cc.campaignBudget || {}), amountMicros: String(op.amountMicros) } } : cc));
      }
      setCampAction(null);
    } catch (e: any) {
      toast.error(`Couldn't apply: ${e?.message || 'error'}`);
    } finally {
      setCampApplying(false);
    }
  }, [campAction, selectedCid, budgetInput]);

  // ── Campaign status filter ─────────────────────────────────────────────
  // Declutter the table by real campaign STATE rather than a manual hide-list:
  // 'active' = ENABLED only (default), 'notServing' = PAUSED/REMOVED only (so
  // they can still be reviewed deliberately), 'all' = everything.
  // Persisted per account in localStorage (per-browser).
  const [statusFilter, setStatusFilter] = useState<CampaignStatusFilter>('active');
  useEffect(() => {
    let v: CampaignStatusFilter = 'active';
    try {
      const raw = selectedCid ? localStorage.getItem(`gads.campaignStatusFilter.${selectedCid}`) : null;
      // 'paused' is the legacy stored value from before the rename.
      if (raw === 'active' || raw === 'all') v = raw;
      else if (raw === 'notServing' || raw === 'paused') v = 'notServing';
    } catch { /* storage unavailable */ }
    setStatusFilter(v);
  }, [selectedCid]);
  const changeStatusFilter = useCallback((v: CampaignStatusFilter) => {
    setStatusFilter(v);
    try { if (selectedCid) localStorage.setItem(`gads.campaignStatusFilter.${selectedCid}`, v); } catch { /* storage blocked */ }
  }, [selectedCid]);

  const [daily, setDaily] = useState<DailyPerformanceRow[]>([]);
  // Derived (cheap; recomputed each render). visibleCampaigns drives the table.
  const notServingCampaignCount = campaigns.reduce((nn, c) => nn + (isCampaignNotServing(c.campaign.status) ? 1 : 0), 0);
  const activeCampaignCount = campaigns.length - notServingCampaignCount;
  const visibleCampaigns = statusFilter === 'all'
    ? campaigns
    : campaigns.filter((c) => isCampaignNotServing(c.campaign.status) === (statusFilter === 'notServing'));
  // Campaign IDs the filter excludes — cascaded into the deep sections so a
  // filtered-out campaign's ad groups / keywords / search terms hide with it.
  // Memoised so the deep sections' memos don't churn every render.
  const filteredOutCampaignIds = useMemo(() => {
    if (statusFilter === 'all') return new Set<string>();
    return new Set(
      campaigns
        .filter((c) => isCampaignNotServing(c.campaign.status) !== (statusFilter === 'notServing'))
        .map((c) => String(c.campaign.id))
    );
  }, [campaigns, statusFilter]);
  /** Selected timeframe — drives the metric queries, the trend chart, and the
   *  auto-backfill. Shared with the portfolio + deep sections. Restored from
   *  the last session's choice (defaults to "today"); persisted on change. */
  const [selectedRange, setSelectedRange] = useState<AdsRange>(loadStoredAdsRange);
  useEffect(() => { storeAdsRange(selectedRange); }, [selectedRange]);
  /** True while the on-demand range backfill is writing the store. */
  const [backfilling, setBackfilling] = useState(false);
  // Surfaces a FAILED daily-store refresh (previously fire-and-forget/swallowed),
  // so stale stored data is never silently presented as current.
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  /** Hint that we should offer a "reconnect" link alongside the error.
   *  Set when the error MESSAGE itself signals auth/credential trouble
   *  (NOT just because the HTTP code was 403 — Google Ads returns 403 for
   *  plenty of non-auth reasons like "no permission to query this
   *  specific customer"). The hint is additive — dataError is always
   *  rendered with Google's actual message regardless. */
  const [suggestReconnect, setSuggestReconnect] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  // Bumped by [reload] so it ALSO re-fetches the metric store (volatile tail),
  // not just the account summary — the KPIs/chart/campaign/daily tables are
  // driven by the daily-store backfill, which otherwise only re-runs on an
  // account/range change.
  const [reloadNonce, setReloadNonce] = useState(0);

  /** Monotonic counter — every loadData call captures the current value
   *  and bails before any setState if the counter has since advanced.
   *  Prevents a slow Account-A response from clobbering an Account-B
   *  view if the user switched accounts mid-load. */
  const runIdRef = useRef(0);

  /** Ref mirror of knownManagers. loadData reads through the ref so its
   *  useCallback identity stays stable across enrichment / discover-then-
   *  eject set mutations — otherwise the effect on [selectedCid, loadData]
   *  fires a duplicate set of 3 parallel queries every time the set
   *  mutates. */
  const knownManagersRef = useRef<Set<string>>(knownManagers);
  useEffect(() => { knownManagersRef.current = knownManagers; }, [knownManagers]);

  /** Ref mirror of managerOf so loadData (stable identity) can read the
   *  current per-account login-customer-id without taking it as a dep. */
  const managerOfRef = useRef<Record<string, string>>(managerOf);
  useEffect(() => { managerOfRef.current = managerOf; }, [managerOf]);


  // ── View mode + URL sync ─────────────────────────────────────────
  // 'portfolio' = cross-client overview (default landing).
  // 'single'    = drill-down into one account (the original page).
  //
  // We persist the choice in the URL ?cid=XXX so deep-links and refreshes
  // land on the right view. Switching the mode is always done by
  // mutating searchParams — the selectedCid effect mirrors that into
  // local state. Single source of truth: the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCid = (searchParams.get('cid') || '').trim();
  // Optional deep-link target tab (e.g. the portfolio "N recs" badge → recommendations).
  const urlFocus = (searchParams.get('focus') || '').trim();
  const viewMode: 'portfolio' | 'single' = urlCid ? 'single' : 'portfolio';

  /** Ref mirror of setSearchParams — react-router's setter identity churns on
   *  every navigation, and loadData's manager auto-eject needs it. Reading
   *  through the ref keeps loadData's useCallback identity stable, otherwise
   *  the [selectedCid, loadData] effect double-fetches on each navigation. */
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => { setSearchParamsRef.current = setSearchParams; }, [setSearchParams]);

  // Mirror urlCid → selectedCid so the existing single-account code path
  // (which reads selectedCid) gets the right value on load + back/fwd.
  useEffect(() => {
    if (urlCid && selectedCid !== urlCid) setSelectedCid(urlCid);
    if (!urlCid && selectedCid) setSelectedCid('');
    // Intentionally only watching urlCid: selectedCid changes here would
    // create a feedback loop with setSelectedCid → useEffect → setSearchParams.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCid]);

  /** Helper for components to switch to single-account view. Updates URL
   *  and lets the mirror effect propagate to selectedCid. */
  const navigateToAccount = useCallback((cid: string, focus?: string) => {
    // Plain open (no deep-link target) → drop any stale tab focus so a focus left
    // over from a previously-viewed account can't hijack this one's initial tab.
    // The focus case (e.g. ?focus=recommendations) seeds a fresh, cid-scoped focus.
    if (!focus) setDeepFocus(null);
    setSearchParams(focus ? { cid, focus } : { cid }, { replace: false });
  }, [setSearchParams]);

  /** Back-to-portfolio handler. Clears the URL param. */
  const navigateToPortfolio = useCallback(() => {
    setSearchParams({}, { replace: false });
  }, [setSearchParams]);

  // ── Display account list = union of two sources ─────────────────
  // listAccessibleCustomers (the `accounts` state) only returns customers
  // the OAuth user has DIRECT access to. If the user is admin on the MCC
  // but isn't a direct user on a specific child account (common — MCC
  // admin grants access transitively without per-child user records),
  // that child is invisible to listAccessibleCustomers but visible to
  // customer_clients_under_manager. The picker has to show BOTH so the
  // operating-account dropdown matches what the user sees in the
  // Google Ads UI's account switcher.
  //
  // Declared up here (above the effects that read it) because hooks
  // execute in declaration order; auto-select depends on this value.
  const displayAccounts = useMemo(() => {
    const seen = new Set<string>();
    const out: AccessibleCustomer[] = [];
    for (const a of accounts || []) {
      if (!a.customerId || seen.has(a.customerId)) continue;
      seen.add(a.customerId);
      out.push(a);
    }
    for (const cid of Object.keys(enriched)) {
      if (seen.has(cid)) continue;
      seen.add(cid);
      out.push({ customerId: cid, resourceName: `customers/${cid}` });
    }
    return out;
  }, [accounts, enriched]);

  // Picker options exclude disabled accounts — except the one currently
  // selected (so a direct navigation to a disabled account's URL still
  // renders a consistent dropdown rather than an orphaned value).
  const pickerAccounts = useMemo(
    () => displayAccounts.filter((a) => !disabledCids.has(a.customerId) || a.customerId === selectedCid),
    [displayAccounts, disabledCids, selectedCid],
  );

  // ── Load accessible customers + config ─────────────────────────────
  // Delegates to the shared loadAdsAccountList helper, which does the
  // multi-manager hierarchy discovery (so accounts under a manager OTHER
  // than the configured one are found) and builds the managerOf map used
  // to route each query through the correct ancestor manager.
  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError(null);
    setSuggestReconnect(false);
    try {
      const r = await loadAdsAccountList();
      if (r.error) {
        const msg = r.error;
        if (looksLikeAdsAuthError(msg)) {
          setSuggestReconnect(true);
        }
        setAccountsError(msg);
        setAccounts([]);
        return;
      }
      setAccounts(r.displayAccounts);
      setManagerCid(r.managerCid);
      setDefaultCid(r.defaultCid);
      setEnriched(r.enriched);
      setKnownManagers(r.knownManagers.size > 0 ? r.knownManagers : new Set([r.managerCid]));
      setManagerOf(r.managerOf);
    } catch (e: any) {
      setAccountsError(e?.message || 'Could not load accessible accounts.');
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => { void loadAccounts(); }, [loadAccounts]);

  // Live subscription to the store's disabled flags — drives the picker +
  // auto-select exclusion. Cheap (one collection, small docs).
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'googleAdsAccounts'),
      (snap) => {
        const s = new Set<string>();
        snap.forEach((d) => { if ((d.data() as any)?.disabled === true) s.add(d.id); });
        setDisabledCids(s);
      },
      (err) => { console.warn('[GoogleAdsPage] disabled-flags sub error:', err); },
    );
    return () => unsub();
  }, []);

  // Live subscription to the daily Google Ads API quota meter
  // (settings/google_ads_quota, written by the sync + query endpoints).
  // Account-agnostic — it tracks total API operations consumed today across
  // the whole developer token.
  const [adsQuota, setAdsQuota] = useState<
    { date?: string; requests?: number; byKind?: { searchStream?: number; listAccessibleCustomers?: number } } | null
  >(null);
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'settings', 'google_ads_quota'),
      (snap) => setAdsQuota(snap.exists() ? (snap.data() as any) : null),
      (err) => { console.warn('[GoogleAdsPage] quota sub error:', err); },
    );
    return () => unsub();
  }, []);

  // ── Auto-select when accounts arrive (no selection yet) ────────────
  // Order of preference:
  //   1. defaultCustomerId from settings (the explicit user choice)
  //   2. First account NOT in knownManagers (operating accounts only)
  //   3. list[0] as a last resort (e.g. user only has access to managers)
  // The knownManagers set is seeded from customer_clients enrichment +
  // the configured MCC, and grows as we discover more (e.g. a summary
  // query returns manager=true). The selectedCid-clearing effect below
  // can also re-trigger this when we auto-eject from a discovered manager.
  useEffect(() => {
    // URL is authoritative. When ?cid=X is present the mirror effect
    // already syncs selectedCid; auto-select must NOT race with it (the
    // fallback would otherwise win on the first render and fire an
    // unwanted fetch on the wrong cid before correcting). So gate strictly
    // on absence of urlCid.
    if (urlCid) return;
    if (viewMode === 'portfolio') return;
    if (selectedCid) return;
    if (displayAccounts.length === 0) return;
    // Don't auto-land on a disabled account (unless it's the explicit
    // configured default).
    if (defaultCid && displayAccounts.some((a) => a.customerId === defaultCid)) {
      setSelectedCid(defaultCid);
      return;
    }
    const operating = displayAccounts.find(
      (a) => !knownManagers.has(a.customerId) && !disabledCids.has(a.customerId),
    );
    setSelectedCid((operating || displayAccounts[0]).customerId);
  }, [displayAccounts, selectedCid, defaultCid, knownManagers, disabledCids, viewMode, urlCid]);

  // ── Load data for the selected account ─────────────────────────────
  const loadData = useCallback(async (cid: string, opts?: { isReload?: boolean }) => {
    if (!cid) return;
    const myRun = ++runIdRef.current;
    setDataLoading(true);
    setDataError(null);
    setSuggestReconnect(false);

    // On account-switch wipe stale data (prevents the new account's KPIs
    // flashing the previous account's numbers). On reload we keep the
    // existing values visible so a refresh isn't a jarring full re-paint.
    if (!opts?.isReload) {
      setSummary(null);
      setCampaigns([]);
      setDaily([]);
    }

    // Manager / MCC short-circuit: managers have no campaigns of their
    // own, and the Ads API rejects metric SELECTs on them with
    // queryError.REQUESTED_METRICS_FOR_MANAGER. Just fetch the account
    // metadata and bail. We hit this branch when either the configured
    // MCC is selected OR enrichment / a prior probe revealed this id to
    // be a manager. Read via ref so loadData identity stays stable as
    // the set mutates (otherwise [selectedCid, loadData] re-runs duplicate
    // fetches on every enrichment landing).
    // Per-account login-customer-id: route the query through the right
    // ancestor manager (multi-MCC hierarchies). Read via ref so loadData
    // stays a stable callback.
    const loginCid = managerOfRef.current[cid];
    if (knownManagersRef.current.has(cid)) {
      try {
        const sumResp = await callQuery<CustomerSummaryRow>(cid, 'customer_summary', loginCid);
        if (runIdRef.current !== myRun) return;
        if (sumResp.ok && sumResp.rows) setSummary(sumResp.rows[0] || null);
        else if (!sumResp.ok) {
          const msg = sumResp.error || `HTTP ${sumResp.httpStatus ?? '???'}`;
          if (sumResp.httpStatus === 401 || looksLikeAdsAuthError(msg)) {
            setSuggestReconnect(true);
          }
          setDataError(`summary: ${msg}`);
        }
        setCampaigns([]);
        setDaily([]);
      } finally {
        if (runIdRef.current === myRun) setDataLoading(false);
      }
      return;
    }

    try {
      // Only the account metadata is fetched live now. The KPIs / campaigns /
      // daily table read the STORED daily collections (range-aware) via the
      // subscriptions below — quota-free, instant once backfilled. Manager
      // detection uses the authoritative customer.manager flag (the old
      // metric-rejection signal is gone with the live metric queries).
      const sumResp = await callQuery<CustomerSummaryRow>(cid, 'customer_summary', loginCid);
      if (runIdRef.current !== myRun) return;

      const summaryReportsManager =
        sumResp.ok && sumResp.rows && sumResp.rows[0]?.customer?.manager === true;
      if (summaryReportsManager) {
        setKnownManagers((prev) => {
          if (prev.has(cid)) return prev;
          const next = new Set(prev);
          next.add(cid);
          return next;
        });
        if (sumResp.rows) setSummary(sumResp.rows[0] || null);
        setCampaigns([]);
        setDaily([]);
        // Clear BOTH selectedCid AND the URL ?cid= so the mirror effect
        // doesn't re-apply the manager id; hands the user to portfolio.
        // (Via ref — keeps loadData's identity stable across navigations.)
        setSelectedCid('');
        setSearchParamsRef.current({}, { replace: true });
        return;
      }

      if (sumResp.ok && sumResp.rows) {
        setSummary(sumResp.rows[0] || null);
      } else if (!sumResp.ok) {
        const msg = sumResp.error || `HTTP ${sumResp.httpStatus ?? '???'}`;
        if (sumResp.httpStatus === 401 || looksLikeAdsAuthError(msg)) {
          setSuggestReconnect(true);
        }
        setDataError(`summary: ${msg}`);
      }
    } catch (e: any) {
      if (runIdRef.current !== myRun) return;
      setDataError(e?.message || 'Could not load account data.');
    } finally {
      if (runIdRef.current === myRun) setDataLoading(false);
    }
  }, [setSearchParams]);

  // Inline OAuth reconnect — runs the consent popup, then re-loads accounts +
  // the current account so the page recovers without a manual refresh. Wired to
  // the reconnect button shown when an error looks credential-related (e.g. the
  // refresh token was revoked/expired → 'invalid_grant').
  const handleReconnect = useCallback(async () => {
    if (reconnecting) return;
    setReconnecting(true);
    try {
      const r = await startGoogleAdsReconnect();
      if (!r.ok) { toast.error(`Reconnect failed: ${r.error || 'unknown error'}`); return; }
      toast.success('Reconnected to Google Ads — reloading…');
      setSuggestReconnect(false);
      setAccountsError(null);
      setDataError(null);
      await loadAccounts();
      if (selectedCid) void loadData(selectedCid, { isReload: true });
    } finally {
      setReconnecting(false);
    }
  }, [reconnecting, loadAccounts, loadData, selectedCid]);

  // Reload the account metadata when the account changes (range-independent).
  useEffect(() => { if (selectedCid) void loadData(selectedCid); }, [selectedCid, loadData]);

  // Persist the selected range to the daily store — the smart incremental
  // "save to Firebase": ensureGoogleAdsRange writes only the days not already
  // stored (recent volatile days always refresh). Fire-and-forget; the live
  // view above renders immediately while this backfills the store for the
  // portfolio, the deep tables, and future visits. Managers have no metrics.
  useEffect(() => {
    if (!selectedCid || knownManagersRef.current.has(selectedCid)) { setBackfilling(false); return; }
    const { from, to } = selectedRange;
    const login = managerOfRef.current[selectedCid];
    // Mark backfilling SYNCHRONOUSLY (before the debounce) so the empty store
    // read during the 600ms window can't flash a premature "no activity".
    // Debounced so clicking through presets fires one backfill, not one per
    // click (each re-fetches the 90-day volatile tail).
    setBackfilling(true);
    const t = setTimeout(() => {
      ensureAdsRangeBackfill(selectedCid, from, to, login)
        .then((r) => {
          if (r.ok) { setBackfillError(null); return; }
          const msg = r.error || 'Could not refresh the latest data.';
          setBackfillError(msg);
          if (looksLikeAdsAuthError(msg)) setSuggestReconnect(true);
        })
        .catch((e) => setBackfillError(e?.message || 'Could not refresh the latest data.'))
        .finally(() => setBackfilling(false));
    }, 600);
    return () => clearTimeout(t);
  }, [selectedCid, selectedRange, reloadNonce]);

  // ── Stored daily reads (range-aware) drive the single-account KPIs,
  //    chart, campaign + daily tables. The backfill above keeps the store
  //    current; these onSnapshots reflect it live and quota-free. Mapped to
  //    the existing wire shapes so the render is unchanged.
  useEffect(() => {
    if (!selectedCid || knownManagers.has(selectedCid)) { setDaily([]); return; }
    const { from, to } = selectedRange;
    const months = monthsBetween(from, to);
    if (months.length === 0) { setDaily([]); return; }
    // Packed store: query the (entity,month) docs spanning the window, then
    // unpack + clamp each to [from,to] before mapping to the wire shape.
    const q = fsQuery(
      collection(db, 'googleAdsAccounts', selectedCid, 'dailyMetrics'),
      where('month', '>=', months[0]), where('month', '<=', months[months.length - 1]),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.flatMap((d) => unpackMonthDoc(d.data(), from, to));
      setDaily(rows.map((r) => mapStoredDaily(r)));
    }, (e) => { console.warn('[GoogleAdsPage] dailyMetrics sub error', e); setDaily([]); });
    return () => unsub();
  }, [selectedCid, selectedRange, knownManagers]);

  useEffect(() => {
    if (!selectedCid || knownManagers.has(selectedCid)) { setCampaigns([]); return; }
    const { from, to } = selectedRange;
    const months = monthsBetween(from, to);
    if (months.length === 0) { setCampaigns([]); return; }
    // Combine the range-aggregated campaignDaily with the campaigns metadata
    // snapshot (for budget). Both are onSnapshot; recompute on either update.
    // campaignDaily is packed by (campaign,month) — query by month, then
    // unpack + clamp to [from,to] before aggregating.
    let dailyDocs: any[] = [];
    let metaMap: Record<string, any> = {};
    const recompute = () => setCampaigns(buildCampaignRows(dailyDocs, metaMap));
    const u1 = onSnapshot(
      fsQuery(collection(db, 'googleAdsAccounts', selectedCid, 'campaignDaily'), where('month', '>=', months[0]), where('month', '<=', months[months.length - 1])),
      (snap) => { dailyDocs = snap.docs.flatMap((d) => unpackMonthDoc(d.data(), from, to)); recompute(); },
      (e) => { console.warn('[GoogleAdsPage] campaignDaily sub error', e); dailyDocs = []; recompute(); },
    );
    const u2 = onSnapshot(
      collection(db, 'googleAdsAccounts', selectedCid, 'campaigns'),
      (snap) => { const m: Record<string, any> = {}; snap.forEach((d) => { m[d.id] = d.data(); }); metaMap = m; recompute(); },
      () => { /* budget enrichment is best-effort */ },
    );
    return () => { u1(); u2(); };
  }, [selectedCid, selectedRange, knownManagers]);

  // ── Zero-fill the requested range ──────────────────────────────────
  // Google's daily reports (and therefore the store) only contain days that
  // HAD activity — a fully-paused account simply has no rows after its last
  // active day. The chart and daily table must render the REQUESTED window,
  // so missing days become explicit zero rows. Clamped at today: no future
  // points are fabricated when the range extends past now.
  const dailyFilled = useMemo(() => {
    const { from, to } = selectedRange;
    if (!from || !to) return daily;
    const today = todayLocalISO();
    const end = to < today ? to : today;
    const days = eachDayISO(from, end);
    if (!days.length) return daily;
    const byDate = new Map(daily.map((r) => [String(r.segments?.date || ''), r]));
    return days.map((date) => byDate.get(date) || ({ segments: { date }, metrics: {} } as any));
  }, [daily, selectedRange]);

  // ── Deterministic daily order (date-desc for table, asc-ready for chart) ─
  // Google's API generally returns segments.date in the ORDER BY direction,
  // but explicit sort guards against any future change. Table renders the
  // most recent first.
  const dailySorted = useMemo(() => {
    return [...dailyFilled].sort((a, b) => String(b.segments.date).localeCompare(String(a.segments.date)));
  }, [dailyFilled]);

  // ── Derived KPIs from the daily time series ────────────────────────
  // Aggregating over `daily` (one row per day, all campaigns) is more
  // accurate than summing the campaigns table — which is capped at
  // LIMIT 100 by cost and could under-count on very large accounts.
  const kpis = useMemo(() => {
    let cost = 0, impressions = 0, clicks = 0, conversions = 0, conversionsValue = 0;
    for (const row of daily) {
      cost += microsToAmount(row.metrics?.costMicros);
      impressions += n(row.metrics?.impressions);
      clicks += n(row.metrics?.clicks);
      conversions += n(row.metrics?.conversions);
      conversionsValue += n(row.metrics?.conversionsValue);
    }
    return { cost, impressions, clicks, conversions, conversionsValue };
  }, [daily]);

  /** ROAS = conversion value / cost. Distinguish three states:
   *  - normal: shows multiplier
   *  - infinite: conversion value with zero cost (rare but legit)
   *  - empty: no spend, no value — show em-dash */
  const roasLabel = useMemo(() => {
    if (kpis.cost > 0 && kpis.conversionsValue === 0) return '—';   // value not tracked (lead-gen)
    if (kpis.cost > 0) return `${(kpis.conversionsValue / kpis.cost).toFixed(2)}x`;
    if (kpis.conversionsValue > 0) return '∞';
    return '—';
  }, [kpis]);

  /** The last day the daily store actually holds data for, and whether that's
   *  behind where it should be. "Complete through yesterday" is the healthy
   *  state — today is always still accruing. */
  const dataThrough = useMemo(() => {
    let max = '';
    for (const row of daily) {
      const d = String(row.segments?.date || '');
      if (d && d > max) max = d;
    }
    if (!max) return null;
    const yesterday = yesterdayLocalISO();
    // Only judge staleness when the selected window actually reaches yesterday;
    // a historical range legitimately ends earlier.
    const expectsYesterday = selectedRange.to >= yesterday;
    return { date: max, stale: expectsYesterday && max < yesterday };
  }, [daily, selectedRange.to]);


  // Daily series for the trend chart (one point per day in the range —
  // including zero-filled days, so a paused account flatlines instead of
  // the x-axis silently ending at the last active day).
  const trendPoints = useMemo<TrendPoint[]>(() => dailyFilled
    .map((row) => ({
      date: String(row.segments?.date || ''),
      cost: microsToAmount(row.metrics?.costMicros),
      clicks: n(row.metrics?.clicks),
      conversions: n(row.metrics?.conversions),
      impressions: n(row.metrics?.impressions),
      conversionsValue: n(row.metrics?.conversionsValue),
    }))
    .filter((p) => p.date), [dailyFilled]);

  // ── Chart metrics: which KPI series the trend chart plots ───────────
  // Toggled by clicking the KPI tiles (ROAS is a ratio — not chartable).
  // Persisted so the preference survives reloads.
  const [chartMetrics, setChartMetrics] = useState<TrendMetric[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('gadsChartMetrics') || '[]');
      const valid = (Array.isArray(saved) ? saved : []).filter((k: any) => k in TREND_METRICS);
      return valid.length ? valid : ['cost', 'conversions'];
    } catch { return ['cost', 'conversions']; }
  });
  const toggleChartMetric = useCallback((key: TrendMetric) => {
    setChartMetrics((prev) => {
      // Last active metric can't be toggled off — an empty chart helps nobody.
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (!next.length) return prev;
      try { localStorage.setItem('gadsChartMetrics', JSON.stringify(next)); } catch { /* best-effort */ }
      return next;
    });
  }, []);

  const currency = summary?.customer?.currencyCode || undefined;

  /** Cost per conversion — the efficiency figure that actually applies to a
   *  lead-gen account, where ROAS is meaningless without conversion values. */
  const cpaLabel = useMemo(() => {
    if (!(kpis.conversions > 0)) return '—';
    return formatCurrency(kpis.cost / kpis.conversions, currency);
  }, [kpis, currency]);

  const isManagerSelected = !!selectedCid && knownManagers.has(selectedCid);

  // ── Reverse lookup: which Customer (if any) is bound to the
  // currently-selected Ads account? Linking back closes the loop —
  // clicking the badge opens that customer's detail page. useAppData
  // is already loaded at the route level so this is essentially free.
  const { customers } = useAppData();
  const boundCustomer = useMemo(() => {
    if (!selectedCid) return null;
    return customers.find((c) => c.googleAdsCustomerId === selectedCid) || null;
  }, [customers, selectedCid]);

  // PortfolioDashboard is now self-contained — it reads the Firestore
  // googleAdsAccounts store directly, so it no longer needs the account
  // list synthesised from this page's state.

  const noActivity =
    !!summary &&
    !dataLoading &&
    !backfilling &&
    !dataError &&
    // A FAILED refresh is not evidence of no activity. Without this, a backfill
    // error stacked "couldn't refresh" directly above "this account has no
    // activity" — two contradictory claims, the second of which is a guess.
    !backfillError &&
    !isManagerSelected &&
    campaigns.length === 0 &&
    daily.length === 0;

  /** We genuinely don't know whether there's activity — the load failed. Distinct
   *  from noActivity so the UI can offer a retry instead of asserting emptiness. */
  const activityUnknown =
    !!summary && !dataLoading && !backfilling && !isManagerSelected &&
    (!!dataError || !!backfillError) &&
    campaigns.length === 0 && daily.length === 0;

  // Alert click-through handler. The deep sections + campaigns table (the click
  // targets) only render when the account has activity in the selected range, so
  // when they're absent give explicit feedback instead of a silent no-op.
  const handleAlertAct = useCallback((a: AlertDoc) => {
    const type = String(a.type || '');
    // The deep sections + campaigns table render exactly when NOT a manager and
    // NOT noActivity (loading states live inside that branch). Mirror that so a
    // click never silently hits an unmounted target.
    const targetsMounted = !isManagerSelected && !noActivity;
    if (!targetsMounted) {
      toast.info(isManagerSelected ? 'Manager accounts have no campaigns to act on.' : 'Switch to a timeframe with activity (e.g. last 30 days) to open this alert.');
      return;
    }
    const focus = (f: { tab: DeepTab; wasteOnly?: boolean; adApproval?: 'disapproved' }) => { focusNonce.current += 1; setDeepFocus({ ...f, cid: selectedCid, nonce: focusNonce.current }); };
    if (type === 'wasted_terms') return focus({ tab: 'searchTerms', wasteOnly: true });
    if (type === 'ads_disapproved') return focus({ tab: 'ads', adApproval: 'disapproved' });
    if (type === 'spend_no_conv' || type === 'conv_drop') return focus({ tab: 'conversions' });
    if (type === 'budget_lost') {
      const worst = campaigns
        .filter((c) => (c.impressionShare?.budgetLost ?? 0) > 0)
        .sort((x, y) => (y.impressionShare?.budgetLost ?? 0) - (x.impressionShare?.budgetLost ?? 0))[0];
      if (worst) { openCampBudget(worst); return; }       // modal opens in place — no scroll needed
      toast.info('Open the capped campaign’s budget from the campaigns table below.');
      campaignsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // spend_spike / spend_drop and anything else → surface the campaigns table.
    campaignsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [campaigns, openCampBudget, isManagerSelected, noActivity, selectedCid]);

  // Deep-link consumer: the portfolio "N recs" badge navigates here with
  // ?focus=recommendations. Once the (non-manager) deep view is mounted, switch it
  // to that tab via the same focus-nonce the alert click-through uses, then strip
  // the param so a later manual tab change or reload isn't overridden.
  useEffect(() => {
    if (!urlCid || !urlFocus) return;
    // Accept ANY deep tab, not just 'recommendations' — the portfolio's alert
    // rows now deep-link here by tab (searchTerms / ads / conversions / …), and
    // a hard-coded single case silently dropped every other one. 'campaigns'
    // isn't a deep tab; it means "scroll to the campaigns table".
    if (!isManagerSelected) {
      if (urlFocus === 'campaigns') {
        campaignsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (DEEP_TABS.has(urlFocus)) {
        focusNonce.current += 1;
        setDeepFocus({ tab: urlFocus as DeepTab, cid: urlCid, nonce: focusNonce.current });
      }
    }
    setSearchParamsRef.current({ cid: urlCid }, { replace: true });
  }, [urlCid, urlFocus, isManagerSelected]);

  // KPIs show "…" while loading the summary OR while the first backfill of an
  // uncached window is still writing the store (don't flash zeros).
  const kpiLoading = dataLoading || (backfilling && daily.length === 0);

  // ── Daily API-quota pill (shown in both header modes) ──────────────
  // Google exposes no remaining-quota signal, so this counts the operations
  // THIS app made today. The "day" is a Pacific calendar bucket — the
  // closest convention to Google's actual rolling-24h window (disclosed in
  // the tooltip). 15,000/day is the Basic Access cap.
  const ADS_DAILY_CAP = 15000;
  const renderQuotaPill = () => {
    const todayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
    // A stale doc (yesterday's bucket) or no doc reads as 0 for "today".
    const fresh = !!adsQuota && adsQuota.date === todayKey;
    const used = fresh ? (adsQuota!.requests || 0) : 0;
    const pct = Math.min(100, Math.round((used / ADS_DAILY_CAP) * 100));
    const tone = pct >= 90 ? 'alert' : pct >= 70 ? 'attention' : 'healthy';
    const ss = fresh ? (adsQuota!.byKind?.searchStream || 0) : 0;
    const la = fresh ? (adsQuota!.byKind?.listAccessibleCustomers || 0) : 0;
    const title =
      `Daily Google Ads API operations consumed today (client-side estimate).\n` +
      `${used.toLocaleString()} of ${ADS_DAILY_CAP.toLocaleString()} — counts only calls this app made ` +
      `(queries ${ss.toLocaleString()}, account lists ${la.toLocaleString()}).\n` +
      `Resets ~midnight Pacific; Google actually meters a rolling 24h window, so treat near-cap as ` +
      `approximate. The authoritative stop signal is a RESOURCE_EXHAUSTED error from Google.`;
    return (
      <>
        <span className="gads-bar__sep">·</span>
        <span className={`gads-bar__quota gads-bar__quota--${tone}`} title={title}>
          api {used.toLocaleString()}/{ADS_DAILY_CAP.toLocaleString()} ({pct}%)
        </span>
      </>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────
  // Two top-level modes branch off the URL ?cid= param:
  //   - portfolio (default): cross-client overview, click row → switch
  //   - single (?cid=XXX):   the original single-account dashboard
  if (viewMode === 'portfolio') {
    return (
      <>
        <PageTitle breadcrumbs={breadCrumbs}>Google Ads</PageTitle>
        <div className="google-ads-page">
          <header className="gads-bar">
            <div className="gads-bar__left">
              <span className="gads-bar__sigil">$</span>
              <span className="gads-bar__path">~/marketing/google-ads/</span>
              <span className="gads-bar__account">all accounts</span>
              {renderQuotaPill()}
            </div>
            {/* Timeframe lives inside PortfolioDashboard now (next to the spend
                chart) and drives the chart + the table together. */}
          </header>
          <PortfolioDashboard onSelectAccount={navigateToAccount} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}>Google Ads</PageTitle>
      <div className="google-ads-page">
        {/* Status / picker bar */}
        <header className="gads-bar">
          <div className="gads-bar__left">
            <span className="gads-bar__sigil">$</span>
            <span className="gads-bar__path">~/marketing/google-ads/</span>
            {/* Account name + number. Prefer the loaded summary name,
                fall back to the enriched (customer_clients) name so we
                show a name even before the summary query lands. The CID
                is shown alongside whenever we have a name (so it's never
                name-XOR-number); when there's no name at all the account
                span shows the number on its own. */}
            {(() => {
              const selName =
                summary?.customer?.descriptiveName ||
                enriched[selectedCid]?.descriptiveName ||
                '';
              return (
                <>
                  <span className="gads-bar__account">
                    {selName
                      ? selName.toLowerCase()
                      : (selectedCid ? prettyCustomerId(selectedCid) : '(no account)')}
                  </span>
                  {selectedCid && selName && (
                    <>
                      <span className="gads-bar__sep">·</span>
                      <span className="gads-bar__meta">{prettyCustomerId(selectedCid)}</span>
                    </>
                  )}
                </>
              );
            })()}
            {summary?.customer?.currencyCode && (
              <>
                <span className="gads-bar__sep">·</span>
                <span className="gads-bar__meta">{summary.customer.currencyCode.toLowerCase()}</span>
              </>
            )}
            {summary?.customer?.timeZone && (
              <>
                <span className="gads-bar__sep">·</span>
                <span className="gads-bar__meta">{summary.customer.timeZone}</span>
              </>
            )}
            {renderQuotaPill()}
            {(summary?.customer?.manager || isManagerSelected) && (
              <>
                <span className="gads-bar__sep">·</span>
                <span className="gads-bar__badge">manager</span>
              </>
            )}
            {summary?.customer?.testAccount && (
              <>
                <span className="gads-bar__sep">·</span>
                <span className="gads-bar__badge gads-bar__badge--warn">test</span>
              </>
            )}
            {/* Reverse-lookup chip — when a Customer record is bound to
                this Ads account, surface a click-through to its detail
                page. Closes the loop between the marketing surface and
                the CRM. */}
            {boundCustomer && (
              <>
                <span className="gads-bar__sep">·</span>
                <Link
                  to={`/contacts?id=${boundCustomer.id}`}
                  className="gads-bar__customer-link"
                  title={`Open ${boundCustomer.name || boundCustomer.company || 'customer'} in CRM`}
                >
                  ↪ {(boundCustomer.name || boundCustomer.company || 'customer').toLowerCase()}
                </Link>
              </>
            )}
            {/* How current the numbers on this page actually are. The bar showed
                currency, timezone and quota but never a timestamp, so a frozen
                daily store was indistinguishable from a quiet account. */}
            {dataThrough && (
              <>
                <span className="gads-bar__sep">·</span>
                <span
                  className={`gads-bar__badge${dataThrough.stale ? ' gads-bar__badge--warn' : ''}`}
                  title={dataThrough.stale
                    ? `The daily store has nothing after ${dataThrough.date}. These numbers are not current — hit [reload] to backfill.`
                    : `Daily data is complete through ${dataThrough.date} (today is still accruing).`}
                >
                  data → {dataThrough.date}
                </span>
              </>
            )}
          </div>
          <div className="gads-bar__right">
            <AdsRangeSelector value={selectedRange} onChange={setSelectedRange} />
            <button
              type="button"
              className="gads-bar__back"
              onClick={navigateToPortfolio}
              title="Back to all accounts"
            >
              ← portfolio
            </button>
            <select
              className="gads-bar__picker"
              value={selectedCid}
              onChange={(e) => navigateToAccount(e.target.value)}
              disabled={accountsLoading || !accounts}
            >
              {accountsLoading && <option value="">loading accounts…</option>}
              {!accountsLoading && pickerAccounts.length === 0 && (
                <option value="">(no accessible accounts)</option>
              )}
              {!accountsLoading && pickerAccounts.map((a) => {
                const meta = enriched[a.customerId];
                const isMgr = knownManagers.has(a.customerId);
                // Label order: descriptive name (if known) → pretty id →
                // role hint. Sub-managers labeled distinctly from the
                // configured top-level MCC so the user sees the structure.
                const name = meta?.descriptiveName;
                const roleLabel = isMgr
                  ? (a.customerId === managerCid ? ' · manager (top)' : ' · sub-manager')
                  : (meta?.testAccount ? ' · test' : '');
                return (
                  <option key={a.customerId} value={a.customerId}>
                    {name ? `${name} · ` : ''}{prettyCustomerId(a.customerId)}{roleLabel}
                  </option>
                );
              })}
            </select>
            <button
              type="button"
              className="gads-bar__reload"
              onClick={() => { if (selectedCid) { void loadData(selectedCid, { isReload: true }); setReloadNonce((n) => n + 1); } }}
              disabled={dataLoading || !selectedCid}
              title="Re-fetch live data for this account"
            >
              {dataLoading ? '[loading…]' : '[reload]'}
            </button>
          </div>
        </header>

        {/* Accounts error — always rendered when set so we never hide
            Google's actual message behind a generic "expired" banner. */}
        {accountsError && (
          <div className="gads-alert gads-alert--err">
            ✗ Could not load accessible accounts: {accountsError}
            {suggestReconnect && (
              <div className="gads-alert__hint">
                Your Google Ads authorization has expired or been revoked.{' '}
                <button type="button" className="gads-alert__reconnect" onClick={handleReconnect} disabled={reconnecting}>
                  {reconnecting ? 'reconnecting…' : 'Reconnect Google Ads'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Per-data-call error — also always shown. The Reconnect hint
            is offered alongside ONLY when the message actually smells
            auth-related (not just because Google sent a 403, which can
            mean many things on the Ads API). */}
        {dataError && (
          <div className="gads-alert gads-alert--warn">
            ⚠ {dataError}
            {suggestReconnect && (
              <div className="gads-alert__hint">
                Your Google Ads authorization has expired or been revoked.{' '}
                <button type="button" className="gads-alert__reconnect" onClick={handleReconnect} disabled={reconnecting}>
                  {reconnecting ? 'reconnecting…' : 'Reconnect Google Ads'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Daily-store refresh failed — stored data may be stale. Distinct from
            dataError (which is the live summary call); shown so a silently-failed
            background refresh can't masquerade as up-to-date numbers. */}
        {backfillError && !dataError && (
          <div className="gads-alert gads-alert--warn">
            ⚠ Couldn't refresh the latest data ({backfillError}) — showing the most recent stored figures.
            {suggestReconnect && (
              <div className="gads-alert__hint">
                Your Google Ads authorization has expired or been revoked.{' '}
                <button type="button" className="gads-alert__reconnect" onClick={handleReconnect} disabled={reconnecting}>
                  {reconnecting ? 'reconnecting…' : 'Reconnect Google Ads'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Proactive alerts for THIS account — rendered above the KPIs (and
            even when the range shows no activity: a spend-drop alert is most
            relevant exactly then). Detection skips manager accounts, so the
            panel naturally renders nothing for an MCC. */}
        {selectedCid && <AccountAlertsPanel customerId={selectedCid} onAct={handleAlertAct} />}

        {/* Manager account short-circuit: skip the dashboard sections and
            tell the user how to proceed. Keeps the status bar visible so
            it's clear which account is selected. */}
        {isManagerSelected ? (
          <div className="gads-alert gads-alert--info">
            ℹ This is a manager (MCC) account — it doesn't run campaigns of its own.
            Pick a child account from the dropdown above to see campaign performance.
          </div>
        ) : activityUnknown ? (
          /* The load FAILED and we have nothing — so we can't claim the account
             is empty. Say what actually happened and offer the retry, instead of
             the old behaviour of asserting "no activity" under an error banner. */
          <div className="gads-alert gads-alert--warn" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span>⚠ Couldn't load performance for {selectedRange.label}, so we don't know whether this account has activity. This is a load failure, not an empty account.</span>
            <button
              type="button"
              className="gads-deep__act-btn"
              disabled={dataLoading || !selectedCid}
              onClick={() => { if (selectedCid) { void loadData(selectedCid, { isReload: true }); setReloadNonce((nn) => nn + 1); } }}
            >
              {dataLoading ? 'retrying…' : 'retry'}
            </button>
          </div>
        ) : noActivity ? (
          /* Empty-but-valid account: one banner instead of three "no data"
              messages stacked, which used to read as "something is broken".
              A no-activity account is the most likely place to want to BUILD
              a first campaign, so surface the wizard entry point here too —
              the campaigns table (which also hosts it) isn't rendered in this
              branch. Hidden on disabled/non-writable accounts. */
          <>
            <div className="gads-alert gads-alert--info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span>ℹ This account has no activity in {selectedRange.label}. You can still set up account structure below (negatives, extensions, conversions, recommendations).</span>
              {!disabledCids.has(selectedCid || '') && (
                <button type="button" className="gads-deep__act-btn" title="Build a new Search campaign (created paused)" onClick={() => setWizardOpen(true)}>+ new campaign</button>
              )}
            </div>
            {/* F24: mount the deep sections even with no activity in range so the
                config tabs (negatives / extensions / conversions / recommendations)
                stay reachable — exactly when the user is most likely setting up
                account structure. The activity-dependent tabs render their own
                empty states. */}
            {selectedCid && (
              <AccountDeepSections
                customerId={selectedCid}
                currency={currency}
                range={selectedRange}
                loginCustomerId={managerOf[selectedCid]}
                focus={deepFocus}
                hiddenCampaigns={filteredOutCampaignIds}
                showHiddenCampaigns={false}
                noActivity
              />
            )}
          </>
        ) : (
          <>
            {/* KPI strip — metrics for the selected range */}
            <section className="gads-kpis">
              <KpiCard label={`cost · ${selectedRange.label}`} value={formatCurrency(kpis.cost, currency)} loading={kpiLoading} metric="cost" active={chartMetrics.includes('cost')} onToggle={toggleChartMetric} />
              <KpiCard label="impressions" value={formatInt(kpis.impressions)} loading={kpiLoading} metric="impressions" active={chartMetrics.includes('impressions')} onToggle={toggleChartMetric} />
              <KpiCard label="clicks" value={formatInt(kpis.clicks)} loading={kpiLoading} metric="clicks" active={chartMetrics.includes('clicks')} onToggle={toggleChartMetric} />
              <KpiCard label="conversions" value={formatDecimal(kpis.conversions, 2)} loading={kpiLoading} metric="conversions" active={chartMetrics.includes('conversions')} onToggle={toggleChartMetric} />
              <KpiCard label="conv. value" value={formatCurrency(kpis.conversionsValue, currency)} loading={kpiLoading} metric="conversionsValue" active={chartMetrics.includes('conversionsValue')} onToggle={toggleChartMetric} />
              <KpiCard label="cost / conv." value={cpaLabel} loading={kpiLoading} />
              <KpiCard label="roas" value={roasLabel} loading={kpiLoading} />
            </section>

            {/* Daily trend chart */}
            <section className="gads-section">
              <h3 className="gads-section__title">── trend · {selectedRange.label}{backfilling ? ' · syncing…' : ''} ──</h3>
              <AdsTrendChart points={trendPoints} currency={currency} metrics={chartMetrics} />
            </section>

            {/* Campaigns table */}
            <section className="gads-section" ref={campaignsRef}>
              <h3 className="gads-section__title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>── campaigns · {selectedRange.label} ──</span>
                {!disabledCids.has(selectedCid || '') && (
                  <button type="button" className="gads-deep__act-btn" title="Build a new Search campaign (created paused)" onClick={() => setWizardOpen(true)}>+ new campaign</button>
                )}
              </h3>
              {(dataLoading || backfilling) && campaigns.length === 0 ? (
                <div className="gads-empty">loading campaigns…</div>
              ) : campaigns.length === 0 ? (
                <div className="gads-empty">no campaigns to show</div>
              ) : (
                <div className="gads-table-wrap">
                  <div className="gads-filterbar">
                    <span className="gads-filterbar__count">
                      showing {visibleCampaigns.length} of {campaigns.length}
                    </span>
                    <div className="gads-filterbar__group">
                      <span className="gads-filterbar__label">status</span>
                      {([
                        { v: 'active' as const,     label: 'active',      count: activeCampaignCount,        title: 'enabled campaigns' },
                        { v: 'notServing' as const, label: 'not serving', count: notServingCampaignCount,    title: 'paused + removed campaigns' },
                        { v: 'all' as const,        label: 'all',         count: campaigns.length,           title: undefined },
                      ]).map((o) => (
                        <button
                          key={o.v}
                          type="button"
                          className="gads-deep__act-btn"
                          aria-pressed={statusFilter === o.v}
                          onClick={() => changeStatusFilter(o.v)}
                          title={o.title}
                        >
                          {o.label} {o.count}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* --actions: the last column (pause/budget) stays pinned while the metrics scroll on mobile */}
                  <table className="gads-table gads-table--actions">
                    <thead>
                      <tr>
                        <th>name</th>
                        <th>status</th>
                        <th>channel</th>
                        <th className="num">impr. share</th>
                        <th className="num">budget · daily</th>
                        <th className="num">cost</th>
                        <th className="num">impr.</th>
                        <th className="num">clicks</th>
                        <th className="num">ctr</th>
                        <th className="num">avg cpc</th>
                        <th className="num">conv.</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCampaigns.map((c) => {
                        const st = String(c.campaign.status || '').toUpperCase();
                        const paused = st === 'PAUSED';
                        const removed = st === 'REMOVED';
                        return (
                        <tr key={c.campaign.id} className={removed ? 'gads-deep__row--removed' : paused ? 'gads-deep__row--paused' : ''}>
                          <td className="name">{c.campaign.name}</td>
                          <td className="status">{lower(c.campaign.status)}</td>
                          <td className="status">{lower(c.campaign.advertisingChannelType)}</td>
                          <td className="num" title="search impression share">{impressionShareLabel(c.impressionShare?.search)}</td>
                          <td className="num">{formatCurrency(microsToAmount(c.campaignBudget?.amountMicros), currency)}</td>
                          <td className="num">{formatCurrency(microsToAmount(c.metrics?.costMicros), currency)}</td>
                          <td className="num">{formatInt(n(c.metrics?.impressions))}</td>
                          <td className="num">{formatInt(n(c.metrics?.clicks))}</td>
                          <td className="num">{formatPercent(n(c.metrics?.ctr))}</td>
                          <td className="num">{formatCurrency(microsToAmount(c.metrics?.averageCpc), currency)}</td>
                          <td className="num">{formatDecimal(n(c.metrics?.conversions), 2)}</td>
                          <td className="status" style={{ whiteSpace: 'nowrap' }}>
                            {removed ? (
                              <span style={{ color: '#777' }} title="Removed campaigns are read-only in Google Ads">removed</span>
                            ) : (
                            /* flex gap (not a text space) so the two live-money buttons keep a real separation on touch */
                            <div style={{ display: 'inline-flex', gap: 8 }}>
                              <button type="button" className="gads-deep__act-btn" onClick={() => openCampStatus(c)}>{paused ? 'enable' : 'pause'}</button>
                              <button type="button" className="gads-deep__act-btn" onClick={() => openCampBudget(c)} title="Edit daily budget">budget</button>
                            </div>
                            )}
                          </td>
                        </tr>
                      ); })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Daily performance table */}
            <section className="gads-section">
              <h3 className="gads-section__title">── daily performance · {selectedRange.label} ──</h3>
              {(dataLoading || backfilling) && dailySorted.length === 0 ? (
                <div className="gads-empty">loading daily totals…</div>
              ) : dailySorted.length === 0 ? (
                <div className="gads-empty">no daily data to show</div>
              ) : (
                <div className="gads-table-wrap">
                  <table className="gads-table">
                    <thead>
                      <tr>
                        <th>date</th>
                        <th className="num">cost</th>
                        <th className="num">impressions</th>
                        <th className="num">clicks</th>
                        <th className="num">ctr</th>
                        <th className="num">conv.</th>
                        <th className="num">conv. value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailySorted.map((row) => {
                        const impr = n(row.metrics?.impressions);
                        const clicks = n(row.metrics?.clicks);
                        const ctr = impr > 0 ? clicks / impr : 0;
                        // Today is a day in progress — without saying so, a low
                        // figure reads as a collapse in performance rather than
                        // "it's 9am".
                        const isToday = row.segments?.date === todayLocalISO();
                        return (
                          <tr key={row.segments.date}>
                            <td>
                              {formatDate(row.segments.date)}
                              {isToday && <span className="gads-partial" title="Today is still in progress — this row will keep rising until midnight (account timezone)."> · partial</span>}
                            </td>
                            <td className="num">{formatCurrency(microsToAmount(row.metrics?.costMicros), currency)}</td>
                            <td className="num">{formatInt(impr)}</td>
                            <td className="num">{formatInt(clicks)}</td>
                            <td className="num">{formatPercent(ctr)}</td>
                            <td className="num">{formatDecimal(n(row.metrics?.conversions), 2)}</td>
                            <td className="num">{formatCurrency(n(row.metrics?.conversionsValue), currency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Deep drill-down + waste finder — reads the synced store
                (search terms, keywords, ads, ad groups, conversions). */}
            {selectedCid && (
              <AccountDeepSections
                customerId={selectedCid}
                currency={currency}
                range={selectedRange}
                loginCustomerId={managerOf[selectedCid]}
                focus={deepFocus}
                hiddenCampaigns={filteredOutCampaignIds}
                showHiddenCampaigns={false}
              />
            )}
          </>
        )}

        {/* Write pipeline for THIS account — proposals awaiting review,
            applied changes with undo, failures, and reverted/rejected
            history. Rendered outside the no-activity short-circuit: an
            account can have change history even with no spend in range.
            Keyed by account (like CampaignWizard below): switching accounts
            remounts the panel so a confirm modal / bulk-progress state from
            account A can never act on or render over account B. */}
        {selectedCid && !isManagerSelected && <ChangeActivityPanel key={selectedCid} accountId={selectedCid} />}
      </div>

      {/* ── Campaign action confirm modal (pause/enable + budget) ────── */}
      {wizardOpen && selectedCid && (
        // Keyed by account: switching accounts (e.g. browser back) unmounts the
        // wizard rather than carrying a draft across to the wrong account.
        <CampaignWizard key={selectedCid} customerId={selectedCid} currency={currency} onClose={() => setWizardOpen(false)} />
      )}

      {campAction && (
        <div className="gads-neg-modal__overlay" onClick={() => !campApplying && setCampAction(null)}>
          <div className="gads-neg-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {campAction.kind === 'status' ? (
              <>
                <div className="gads-neg-modal__title">{String(campAction.c.campaign.status || '').toUpperCase() === 'PAUSED' ? 'Enable' : 'Pause'} campaign</div>
                <div className="gads-neg-modal__body">{String(campAction.c.campaign.status || '').toUpperCase() === 'PAUSED' ? 'Enable' : 'Pause'} campaign <strong>{campAction.c.campaign.name}</strong>?</div>
              </>
            ) : (
              <>
                <div className="gads-neg-modal__title">Edit daily budget</div>
                <div className="gads-neg-modal__body">
                  Set the daily budget for <strong>{campAction.c.campaign.name}</strong>:
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{currency || ''}</span>
                    <input type="number" inputMode="decimal" min="0" step="0.01" value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)} className="gads-neg-modal__input" autoFocus />
                    <span style={{ color: '#888', fontSize: 11 }}>/ day</span>
                  </div>
                  <div style={{ color: '#888', fontSize: 11, marginTop: 6 }}>Currently {formatCurrency(microsToAmount(campAction.c.campaignBudget?.amountMicros), currency)}/day.</div>
                </div>
              </>
            )}
            <div className="gads-neg-modal__warn">⚠ Applies immediately to the LIVE Google Ads account. Undo from “change activity” at the bottom of this page.{campAction.kind === 'budget' ? ' (A shared budget affects every campaign linked to it.)' : ''}</div>
            <div className="gads-neg-modal__actions">
              <button type="button" className="gads-neg-modal__cancel" disabled={campApplying} onClick={() => setCampAction(null)}>Cancel</button>
              <button type="button" className="gads-neg-modal__apply" disabled={campApplying} onClick={applyCampAction}>{campApplying ? 'Applying…' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

/** KPI tile. When `metric` is given the tile is a TOGGLE for that series on
 *  the trend chart (dot = the series colour; dimmed = currently hidden).
 *  Ratio KPIs (roas) pass no metric and stay plain tiles. */
const KpiCard: React.FC<{
  label: string;
  value: string;
  loading?: boolean;
  metric?: TrendMetric;
  active?: boolean;
  onToggle?: (key: TrendMetric) => void;
}> = ({ label, value, loading, metric, active, onToggle }) => {
  const togglable = !!metric && !!onToggle;
  const color = metric ? TREND_METRICS[metric].color : undefined;
  const cls = [
    'gads-kpi',
    loading ? 'gads-kpi--loading' : '',
    togglable ? 'gads-kpi--togglable' : '',
    togglable && active ? 'gads-kpi--active' : '',
    togglable && !active ? 'gads-kpi--off' : '',
  ].filter(Boolean).join(' ');
  const body = (
    <>
      <div className="gads-kpi__label">
        {togglable && <span className="gads-kpi__dot" style={{ background: active ? color : 'transparent', borderColor: color }} />}
        {label}
      </div>
      <div className="gads-kpi__value">{loading ? '…' : value}</div>
    </>
  );
  if (!togglable) return <div className={cls}>{body}</div>;
  return (
    <button
      type="button"
      className={cls}
      style={active ? { borderBottom: `2px solid ${color}` } : undefined}
      title={active ? `Hide ${label} on the trend chart` : `Show ${label} on the trend chart`}
      aria-pressed={active}
      onClick={() => onToggle!(metric!)}
    >
      {body}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// API helper — typed wrapper around POST /googleAdsQuery
// ─────────────────────────────────────────────────────────────────────────

// ── Stored-daily → wire-shape mappers ────────────────────────────────────
// The render consumes the live wire shapes (DailyPerformanceRow / CampaignRow).
// The store keeps cost in DOLLARS, so map cost → costMicros for the existing
// microsToAmount round-trip and keep the render code unchanged.
function num2(v: any): number { const x = Number(v); return isFinite(x) ? x : 0; }
/** Impression-share fraction → label. null/undefined → '—'; 0.0999 sentinel → '<10%'. */
function impressionShareLabel(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v <= 0.0999) return '<10%';
  return `${Math.round(v * 100)}%`;
}

function mapStoredDaily(x: any): DailyPerformanceRow {
  return {
    segments: { date: String(x?.date || '') },
    metrics: {
      costMicros: String(Math.round(num2(x?.cost) * 1e6)),
      impressions: String(num2(x?.impressions)),
      clicks: String(num2(x?.clicks)),
      conversions: num2(x?.conversions),
      conversionsValue: num2(x?.conversionsValue),
    },
  };
}

/** Local YYYY-MM-DD (en-CA locale formats as ISO). */
/** Deep-view tabs reachable via ?focus= (mirrors AccountDeepSections' Tab). */
const DEEP_TABS: Set<string> = new Set([
  'searchTerms', 'keywords', 'ads', 'adGroups', 'negatives', 'assets',
  'recommendations', 'conversions', 'landingPages', 'device', 'geo', 'hours', 'demographics',
]);

function todayLocalISO(): string { return new Date().toLocaleDateString('en-CA'); }
/** Yesterday, same local basis as todayLocalISO — the last COMPLETE day. */
function yesterdayLocalISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-CA');
}

/** Every YYYY-MM-DD from `from` to `to` inclusive. Bounded to ~400 days. */
function eachDayISO(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(from + 'T00:00:00Z');
  const end = new Date(to + 'T00:00:00Z');
  if (isNaN(d.getTime()) || isNaN(end.getTime())) return out;
  for (let i = 0; i < 400 && d <= end; i++) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function buildCampaignRows(dailyDocs: any[], metaMap: Record<string, any>): CampaignRow[] {
  const agg = new Map<string, any>();
  for (const x of dailyDocs) {
    const id = String(x?.campaignId || ''); if (!id) continue;
    let e = agg.get(id);
    if (!e) { e = { id, name: '', status: '', channelType: '', cost: 0, impressions: 0, clicks: 0, conversions: 0, conversionsValue: 0 }; agg.set(id, e); }
    e.cost += num2(x.cost); e.impressions += num2(x.impressions); e.clicks += num2(x.clicks);
    e.conversions += num2(x.conversions); e.conversionsValue += num2(x.conversionsValue);
    if (x.campaignName) e.name = x.campaignName; if (x.status) e.status = x.status; if (x.channelType) e.channelType = x.channelType;
  }
  // Campaigns with ZERO activity in the range have no daily rows at all —
  // include them from the metadata snapshot with explicit zeros so a paused
  // (or simply quiet) campaign doesn't vanish from the table. REMOVED campaigns
  // are included too (marked + read-only in the UI) so removed isn't invisible;
  // the metadata snapshot soft-prunes after ~35 days so this is recently-removed.
  for (const id of Object.keys(metaMap)) {
    if (agg.has(id)) continue;
    const meta = metaMap[id] || {};
    agg.set(id, { id, name: meta.name || '', status: meta.status || '', channelType: meta.channelType || '', cost: 0, impressions: 0, clicks: 0, conversions: 0, conversionsValue: 0 });
  }
  return Array.from(agg.values()).map((e) => {
    const meta = metaMap[e.id] || {};
    const ctr = e.impressions > 0 ? e.clicks / e.impressions : 0;
    const avgCpcMicros = e.clicks > 0 ? Math.round((e.cost / e.clicks) * 1e6) : 0;
    return {
      campaign: { id: e.id, name: e.name || meta.name || '', status: e.status || meta.status || '', advertisingChannelType: e.channelType || meta.channelType || '' },
      impressionShare: meta.impressionShare || null,
      campaignBudget: { amountMicros: String(num2(meta.budgetMicros)) },
      metrics: { costMicros: String(Math.round(e.cost * 1e6)), impressions: String(e.impressions), clicks: String(e.clicks), conversions: e.conversions, conversionsValue: e.conversionsValue, ctr, averageCpc: String(avgCpcMicros) },
    } as CampaignRow;
  }).sort((a, b) => Number(b.metrics?.costMicros || 0) - Number(a.metrics?.costMicros || 0));
}

async function callQuery<T>(
  customerId: string,
  queryName: string,
  loginCustomerId?: string,
  params?: Record<string, string>,
): Promise<QueryResponse<T>> {
  try {
    const payload: Record<string, unknown> = { customerId, queryName };
    if (loginCustomerId) payload.loginCustomerId = loginCustomerId;
    if (params && Object.keys(params).length > 0) payload.params = params;
    const r = await authFetch(`${CF_BASE}/googleAdsQuery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const text = await r.text();
    let data: any = null;
    try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || !data?.ok) {
      return {
        ok: false,
        error: data?.error || `HTTP ${r.status}`,
        // Critical: propagate the parsed GoogleAdsFailure code so the
        // discover-then-eject branch in loadData can detect
        // queryError.REQUESTED_METRICS_FOR_MANAGER. Without this, the
        // auto-eject branch is dead code and users land stuck on a
        // sub-MCC with a generic "permission" error.
        errorCode: data?.errorCode,
        httpStatus: r.status,
      };
    }
    return { ok: true, rows: Array.isArray(data.rows) ? data.rows : [] };
  } catch (e: any) {
    return { ok: false, error: e?.message || 'Request failed' };
  }
}

export default GoogleAdsPage;
